import React, { useEffect, useState, useMemo } from 'react';
import { MAPBOX_TOKEN } from '../../config/mapStyles';

/**
 * WeatherPill — floating top app bar from the Stitch design.
 * Shows LOC / TMP / WND / SKY using the free Open-Meteo API
 * (no key required) + Mapbox / Nominatim reverse geocoding for the place name.
 */

// In-memory caches to prevent rapid API hammering across drone coordinate ticks
const locCache = new Map();
const wxCache = new Map();
let nominatimBlockedUntil = 0;
let lastNominatimRequestTime = 0;

async function reverseGeocode(lat, lon) {
    const latNum = Number(lat);
    const lonNum = Number(lon);
    if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return null;

    // 1. Prefer Mapbox Reverse Geocoding when token is configured (proper CORS, fast, generous rate limit)
    if (MAPBOX_TOKEN) {
        try {
            const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lonNum.toFixed(4)},${latNum.toFixed(4)}.json?types=place,locality,district&access_token=${MAPBOX_TOKEN}`;
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                const feature = data?.features?.[0];
                if (feature?.text) {
                    return feature.text.toUpperCase();
                }
            }
        } catch { /* fallback to Nominatim */ }
    }

    // 2. Nominatim fallback with strict rate limiting & 429 backoff
    const now = Date.now();
    if (now < nominatimBlockedUntil) {
        return null;
    }
    // Respect OSM 1 req/sec policy (enforce at least 2s gap)
    if (now - lastNominatimRequestTime < 2000) {
        return null;
    }
    lastNominatimRequestTime = now;

    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latNum.toFixed(3)}&lon=${lonNum.toFixed(3)}&zoom=10`;
        const res = await fetch(url);
        if (res.status === 429) {
            // Block further calls to Nominatim for 5 minutes
            nominatimBlockedUntil = Date.now() + 5 * 60 * 1000;
            return null;
        }
        if (res.ok) {
            const data = await res.json();
            const a = data?.address || {};
            const name = a.city || a.town || a.village || a.county || a.state_district || a.state || '';
            if (name) return name.toUpperCase();
        }
    } catch { /* fail silent */ }

    // Fallback if in Nagpur area
    if (Math.abs(latNum - 21.1458) < 0.25 && Math.abs(lonNum - 79.0882) < 0.25) {
        return 'NAGPUR';
    }
    return null;
}

const SKY_BY_CODE = (code) => {
    if (code === 0) return 'CLEAR';
    if (code === 1) return 'M.CLEAR';
    if (code === 2) return 'P.CLOUDY';
    if (code === 3) return 'OVERCAST';
    if (code === 45 || code === 48) return 'FOG';
    if (code >= 51 && code <= 57) return 'DRIZZLE';
    if (code >= 61 && code <= 67) return 'RAIN';
    if (code >= 71 && code <= 77) return 'SNOW';
    if (code >= 80 && code <= 82) return 'SHOWERS';
    if (code >= 95) return 'STORM';
    return '—';
};

function Divider() {
    return <div style={{ width: 1, height: 16, background: 'color-mix(in srgb, var(--borderSecondary) 40%, transparent)' }} />;
}

function Stat({ label, value }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="stitch-label wx-label" style={{ fontSize: 10, color: 'var(--stitch-outline)', textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                {label}
            </span>
            <span className="stitch-label" style={{ fontSize: 13, color: 'var(--accent)' }}>
                {value}
            </span>
        </div>
    );
}

const SKY_ICON = { CLEAR: 'sunny', 'M.CLEAR': 'sunny', 'P.CLOUDY': 'partly_cloudy_day', OVERCAST: 'cloud', FOG: 'foggy', DRIZZLE: 'rainy', RAIN: 'rainy', SHOWERS: 'rainy', SNOW: 'weather_snowy', STORM: 'thunderstorm' };

function WeatherPill({ lat = 21.1458, lon = 79.0882, style = {}, compact = false }) {
    // 0.1 degree grid key (~11 km). Drones moving within city limits will NOT trigger new lookups!
    const gridKey = useMemo(() => {
        const la = Number(lat);
        const lo = Number(lon);
        return (Number.isFinite(la) && Number.isFinite(lo)) ? `${la.toFixed(1)},${lo.toFixed(1)}` : '21.1,79.1';
    }, [lat, lon]);

    const [wx, setWx] = useState(() => wxCache.get(gridKey) || { temp: null, wind: null, sky: null });
    const [loc, setLoc] = useState(() => locCache.get(gridKey) || (gridKey === '21.1,79.1' ? 'NAGPUR' : ''));

    // Current weather — keyed to ~11km grid, refreshed every 10 mins
    useEffect(() => {
        let cancelled = false;
        const [gLat, gLon] = gridKey.split(',');

        const load = async () => {
            if (wxCache.has(gridKey)) {
                setWx(wxCache.get(gridKey));
            }
            try {
                const url = `https://api.open-meteo.com/v1/forecast?latitude=${gLat}&longitude=${gLon}&current=temperature_2m,wind_speed_10m,weather_code`;
                const res = await fetch(url);
                const data = await res.json();
                if (!cancelled && data?.current) {
                    const result = {
                        temp: Math.round(data.current.temperature_2m),
                        wind: data.current.wind_speed_10m,
                        sky: SKY_BY_CODE(data.current.weather_code),
                    };
                    wxCache.set(gridKey, result);
                    setWx(result);
                }
            } catch { /* weather is decorative — fail silent */ }
        };

        load();
        const t = setInterval(load, 10 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [gridKey]);

    // Place name — once per 11km grid change
    useEffect(() => {
        let cancelled = false;
        if (locCache.has(gridKey)) {
            setLoc(locCache.get(gridKey));
            return;
        }

        const [gLat, gLon] = gridKey.split(',');
        const load = async () => {
            const name = await reverseGeocode(gLat, gLon);
            if (!cancelled && name) {
                locCache.set(gridKey, name);
                setLoc(name);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [gridKey]);

    if (compact) {
        // Phone pill from the Flutter app: values only, place name in cyan.
        const sky = wx.sky || '—';
        const skyWord = ({ 'M.CLEAR': 'Clear', 'P.CLOUDY': 'Cloudy' })[sky] || sky.charAt(0) + sky.slice(1).toLowerCase();
        return (
            <div className="m-glass neon m-weather-pill" style={style}>
                <b>{loc ? loc.charAt(0) + loc.slice(1).toLowerCase() : '—'}</b>
                <i />
                <span>{wx.temp != null ? `${wx.temp}°C` : '—'}</span>
                <i />
                <span>{wx.wind != null ? `${Math.round(wx.wind)}km/h` : '—'}</span>
                <i />
                <span className="material-symbols-outlined" style={{ fontSize: 13, color: '#FACC15', fontVariationSettings: "'FILL' 1" }}>{SKY_ICON[sky] || 'cloud'}</span>
                <span>{skyWord}</span>
            </div>
        );
    }

    return (
        <div
            className="wx-pill"
            style={{
                height: 44,
                background: 'rgba(13, 14, 15, 0.8)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                borderRadius: 999,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                padding: '0 22px',
                pointerEvents: 'auto',
                ...style,
            }}
        >
            <Stat label="LOC" value={loc || '—'} />
            <Divider />
            <Stat label="TMP" value={wx.temp != null ? `${wx.temp}°C` : '—'} />
            <Divider />
            <Stat label="WND" value={wx.wind != null ? `${wx.wind}km/h` : '—'} />
            <Divider />
            <Stat label="SKY" value={wx.sky || '—'} />
        </div>
    );
}

export default WeatherPill;
